import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { reviewsApi } from '@/api/client';
import { formatDate } from '@lytmaster/shared';
import { Brain, Calendar, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

export default function WeeklyReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['reviews-list'],
    queryFn: reviewsApi.list,
  });

  const reviewId = id || null;

  const { data: latestData, isLoading: latestLoading } = useQuery({
    queryKey: ['review-latest'],
    queryFn: reviewsApi.getLatest,
    enabled: !reviewId,
  });

  const { data: specificData, isLoading: specificLoading } = useQuery({
    queryKey: ['review', reviewId],
    queryFn: () => reviewsApi.getById(reviewId as any),
    enabled: !!reviewId,
  });

  const isLoading = listLoading || latestLoading || specificLoading;
  const activeReview = reviewId ? specificData?.review : latestData?.review;
  const reviews = listData?.reviews || [];

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6 fade-in">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">AI Cost Reviews</h1>
        <p className="text-slate-500 mt-1">Weekly optimization reports powered by AI</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
              <h3 className="text-sm font-medium text-slate-700">Past Reviews</h3>
            </div>
            {reviews.length === 0 ? (
              <div className="p-4 text-sm text-slate-400 text-center">
                No reviews yet. The first review will be generated automatically on Monday.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {reviews.map((r: any) => (
                  <button
                    key={r.id}
                    onClick={() => navigate(`/reviews/${r.id}`)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors ${
                      activeReview?.id === r.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-700 truncate">{formatDate(r.reviewDate)}</div>
                      <div className="text-xs text-slate-400">{r.modelUsed}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-3">
          {activeReview ? (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                  <Brain className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-900">
                    Cost Optimization Review — {formatDate(activeReview.reviewDate)}
                  </h2>
                  <p className="text-xs text-slate-400">Model: {activeReview.modelUsed}</p>
                </div>
              </div>
              <div className="prose prose-slate max-w-none prose-headings:text-slate-900 prose-p:text-slate-600 prose-li:text-slate-600 prose-strong:text-slate-800">
                <ReactMarkdown>{activeReview.reportMarkdown}</ReactMarkdown>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Brain className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Reviews Available</h3>
              <p className="text-slate-400 max-w-md mx-auto">
                AI-powered cost optimization reviews are generated every Monday at 9 AM UTC.
                Check back after the first scheduled run.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
